import type { ReactNode } from 'react';
import CheckboxGroup from '../../components/form/CheckboxGroup/CheckboxGroup';
import { DateInputField } from '../../components/form/DateInputField/DateInputField';
import RadioGroup from '../../components/form/RadioGroup/RadioGroup';
import SelectInputField from '../../components/form/SelectInputField';
import TextInputField from '../../components/form/TextInputField';
import Textarea from '../../components/form/Textarea';
import EmailList from './EmailList';
import type { EmailQuestion } from './types';

export default function EmailFormQuestion({ question }: { question: EmailQuestion }) {
  const id = `email-form-${question.name}`;
  const rules = { ...question.rules, reservedCharacters: '' };
  const shared = { id, name: question.name, title: question.title, rules };
  const grouped = ['radio', 'checkbox', 'date'].includes(question.questionType);
  if (question.questionType === 'content') return <p className="govuk-body">{question.title}</p>;
  if (question.questionType === 'emailList') return <EmailList question={question} />;
  let input: ReactNode;
  switch (question.questionType) {
    case 'textarea':
      input = (
        <Textarea
          {...shared}
          textRows={question.textRows}
          characterCount={question.characterCount}
          maxLength={question.rules?.maxLength ?? question.characterCount}
        />
      );
      break;
    case 'textInput':
      input = (
        <TextInputField
          {...shared}
          restrictDomains={false}
          characterCount={question.characterCount}
          maxLength={question.rules?.maxLength}
        />
      );
      break;
    case 'select':
      input = (
        <SelectInputField
          {...shared}
          options={question.options || []}
          autoFillSingleOptionAndHide={false}
        />
      );
      break;
    case 'radio':
      input = <RadioGroup id={id} anchorId={id} question={{ ...question, questionType: question.questionType, rules }} />;
      break;
    case 'checkbox':
      input = <CheckboxGroup id={id} anchorId={id} question={{ ...question, questionType: question.questionType, rules }} />;
      break;
    case 'date':
      input = <DateInputField anchorId={id} question={{ ...question, questionType: question.questionType, rules }} />;
      break;
    default:
      throw new Error('Unsupported email form component');
  }
  const hint = question.hint || question.description;
  return (
    <div className={`govuk-form-group${question.width ? ` govuk-!-width-${question.width}` : ''}`}>
      {grouped ? (
        <fieldset className="govuk-fieldset">
          <legend className="govuk-fieldset__legend">{question.title}</legend>
          {hint && <p className="govuk-hint">{hint}</p>}
          {input}
        </fieldset>
      ) : (
        <>
          <label className="govuk-label" htmlFor={id}>
            {question.title}
          </label>
          {hint && <p className="govuk-hint">{hint}</p>}
          {input}
        </>
      )}
    </div>
  );
}
